<!-- @format -->
<template>
    <div class="journey-enable-widget">
        <loading-svg v-if="loading" />
        <div v-else class="widget-contents-container">
            <div class="widget-header">
                <h3>Enable Journey</h3>
                <div class="svg-container widget-close-button" @click="closeWidget">
                    <widget-close-svg />
                </div>
            </div>
            <div class="content-wrapper">
                <div class="section">
                    <div class="label-container">
                        <div class="label-content">
                            <div class="title">Include Current Contacts</div>
                        </div>
                    </div>
                    <div class="input-container checkbox">
                        <div class="checkbox-section">
                            <app-checkbox v-model="includeContacts" />
                            <label>
                                Would you like the contacts already in your selected list(s) to be
                                added to this journey?
                            </label>
                        </div>
                    </div>
                </div>
                <div class="section">
                    <div class="label-container">
                        <div class="label-content">
                            <div class="title">When should the journey start?</div>
                        </div>
                    </div>
                    <app-input-radio v-model="selectedOption" :options="options" />
                    <div class="schedule-journey" v-if="selectedOption.value === 'Schedule'">
                        <app-date-picker v-model="date" :placeholder="'Send Date'" />
                        <time-picker v-model="time" />
                        <timezone-picker v-model="timezone" />
                        <div v-if="!validSendDate" class="error-message">
                            The selected date and time is invalid. Please choose a date and time in
                            the future.
                        </div>
                    </div>
                </div>
            </div>
            <div class="widget-footer">
                <div class="cancel-container">
                    <app-button class="transparent-btn" @click="closeWidget"> Cancel </app-button>
                </div>
                <div class="action-container">
                    <app-button @click="save()">Save</app-button>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
const AppButton = () => import('@/components/common/app-button.vue');
const WidgetCloseSvg = () => import('@/components/svg/quick-add/widget-close-svg.vue');
const LoadingSvg = () => import('@/components/svg/loading-screen-svg');
const AppCheckbox = () => import('@/components/common/app-checkbox');
const AppInputRadio = () => import('@/components/common/app-input-radio');
const AppDatePicker = () => import('@/components/common/app-date-picker');
const TimezonePicker = () => import('@/components/common/timezone-picker');
const TimePicker = () => import('@/components/common/time-picker');

import EventBus from '@/scripts/event-bus.js';

export default {
    name: 'journey-enable-widget',

    props: {
        widget: {
            type: Object,
            required: true,
        },
    },

    components: {
        'app-button': AppButton,
        'widget-close-svg': WidgetCloseSvg,
        'loading-svg': LoadingSvg,
        'app-checkbox': AppCheckbox,
        'app-input-radio': AppInputRadio,
        'app-date-picker': AppDatePicker,
        'timezone-picker': TimezonePicker,
        'time-picker': TimePicker,
    },

    data() {
        var options = [
            {
                name: 'Now',
                value: 'Now',
            },
            {
                name: 'Schedule',
                value: 'Schedule',
            },
        ];

        return {
            loading: false,
            journeyId: this.widget.journeyId,
            includeContacts: false,
            selectedOption: options[1],
            options: options,
            date: new Date(),
            time: this.$moment().format('HH:mm'),
            timezone: this.$helpers.guessTimezone(),
        };
    },

    computed: {
        sendDate() {
            if (this.selectedOption === this.options[1]) {
                var year = this.date.getFullYear();
                var month = ('0' + (this.date.getMonth() + 1)).slice(-2);
                var day = ('0' + this.date.getDate()).slice(-2);

                return this.$moment.tz(
                    `${year}-${month}-${day} ${this.formattedTime}`,
                    this.timezone.name
                );
            } else {
                return null;
            }
        },

        validSendDate() {
            return this.sendDate.isAfter(
                this.$moment(new Date()).local().subtract(1, 'minute'),
                'minute'
            );
        },

        formattedTime() {
            return this.$moment.utc(this.time, 'hh:mma').format('HH:mm');
        },
    },

    methods: {
        closeWidget() {
            EventBus.$emit('close-widget', this.widget);
        },

        save() {
            if (this.sendDate) {
                if (this.validSendDate) {
                    this.schedule();
                }
            } else {
                this.enable();
            }
        },

        enable() {
            this.loading = true;

            var request = {
                addExistingContactsToJourney: this.includeContacts,
            };

            this.$api
                .post(`/api/Journeys/${this.journeyId}/Enable`, request)
                .then(() => {
                    this.$helpers.showSuccessNotification(`Your journey has been enabled.`);

                    EventBus.$emit('journey:update');
                    EventBus.$emit('close-widget', this.widget);
                })
                .catch((error) => {
                    this.$helpers.showFailNotification(`Your journey failed to save.`, { error });
                })
                .finally(() => {
                    this.loading = false;
                });
        },

        schedule() {
            this.loading = true;

            var request = {
                addExistingContactsToJourney: this.includeContacts,
                scheduledDate: this.sendDate.utc().format('YYYY-MM-DDTHH:mm:ss') + 'z',
            };

            this.$api
                .post(`/api/Journeys/${this.journeyId}/Schedule`, request)
                .then(() => {
                    this.$helpers.showSuccessNotification(`Your journey has been scheduled.`);

                    EventBus.$emit('journey:update');
                    EventBus.$emit('close-widget', this.widget);
                })
                .catch((error) => {
                    this.$helpers.showFailNotification(`Your journey failed to save.`, { error });
                })
                .finally(() => {
                    this.loading = false;
                });
        },
    },

    created() {
        const today = new Date();
        let tomorrow = new Date();
        tomorrow.setDate(today.getDate() + 1);

        this.date = tomorrow;
    },
};
</script>

<style>
.journey-enable-widget .transparent-btn {
    background: transparent;
    color: #877d88;
}

.journey-enable-widget .transparent-btn:hover {
    background: #e1e0e5;
}

.journey-enable-widget .section {
    padding: 20px;
    position: relative;
    border: 2px solid #e9e9ef;
    border-radius: 22px;
    margin-bottom: 45px;
}

.journey-enable-widget .label-container {
    display: flex;
    align-items: center;
    margin: 0 auto;
    position: absolute;
    justify-content: center;
    font-weight: 600;
    left: 0;
    width: 100%;
    top: -15px;
}

.journey-enable-widget .title {
    display: flex;
    align-items: center;
}

.journey-enable-widget .label-content {
    display: flex;
    align-items: center;
    background: #fff;
    padding: 0 20px;
}

.journey-enable-widget .input-container {
    width: 100%;
    background: #fff;
    z-index: 10;
    position: relative;
}

.journey-enable-widget .app-checkbox {
    margin-right: 20px;
}

.journey-enable-widget .checkbox-section {
    display: flex;
    align-items: center;
}

.journey-enable-widget .input-container.checkbox {
    background: #fff;
    padding-bottom: 5px;
    padding-left: 0px;
    margin-bottom: 10px;
}

.journey-enable-widget .radio-btn-container {
    margin: 0 30px;
}

.journey-enable-widget .app-input-radio {
    display: flex;
    justify-content: space-between;
    max-width: 250px;
    margin: 0 auto;
}

.journey-enable-widget .vdp-datepicker,
.journey-enable-widget .time-picker {
    margin-bottom: 10px;
}

.journey-enable-widget .meridian {
    margin-left: 10px;
}

.journey-enable-widget .colon {
    padding-bottom: 6px;
    margin: 0 5px;
}

.journey-enable-widget .schedule-journey .error-message {
    margin: 15px 0 0;
    text-align: center;
    color: #f30070;
}
</style>
